import { TFunction } from 'i18next'
import { z } from 'zod'

export type MaintenanceSettingsProps = {
  onClose: () => void
  buildingId: number
}

const weekScheduleDaySchema = z.object({
  enabled: z.boolean(),
  endTime: z.number().nullable(),
  name: z.string(),
  startTime: z.number().nullable(),
})

// Note: the schedule is only validated while the toggle is ON - turning it OFF keeps the
// previously entered schedule in the form (and payload) so it's restored when re-enabled.
export const makeMaintenanceSettingsFormSchema = (t: TFunction<['maintenance']>) =>
  z
    .object({
      businessHours: z.array(weekScheduleDaySchema),
      requiredAssignee: z.boolean(),
      requiredScheduledDate: z.boolean(),
      slotLength: z.number().nullable(),
      suggestedTimeframeEnabled: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!data.suggestedTimeframeEnabled) return

      if (data.slotLength == null) {
        ctx.addIssue({
          code: 'custom',
          message: t('maintenance:settings.validation.slotLengthRequired'),
          path: ['slotLength'],
        })
      }

      if (!data.businessHours.some((day) => day.enabled)) {
        ctx.addIssue({
          code: 'custom',
          message: t('maintenance:settings.validation.atLeastOneDay'),
          path: ['businessHours'],
        })
      }

      data.businessHours.forEach((day, index) => {
        if (!day.enabled) return

        if (day.startTime == null) {
          ctx.addIssue({
            code: 'custom',
            message: t('maintenance:settings.validation.startTimeRequired'),
            path: ['businessHours', index, 'startTime'],
          })
        }
        if (day.endTime == null) {
          ctx.addIssue({
            code: 'custom',
            message: t('maintenance:settings.validation.endTimeRequired'),
            path: ['businessHours', index, 'endTime'],
          })
        }
        // Note: a day window shorter than one slot would leave that day with no bookable slots.
        if (
          day.startTime != null &&
          day.endTime != null &&
          data.slotLength != null &&
          day.endTime - day.startTime < data.slotLength * 60
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('maintenance:settings.validation.hoursShorterThanSlot'),
            path: ['businessHours', index, 'endTime'],
          })
        }
      })
    })

export type MaintenanceSettingsForm = z.infer<ReturnType<typeof makeMaintenanceSettingsFormSchema>>
